import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ory } from 'src/providers/authProvider';
import { Logger } from 'src/utils/Logger';
import { useSettingsFlow } from 'src/auth/hooks/useSettingsFlow';
import { ChangePasswordForm } from 'src/auth/settings/ChangePasswordForm';
import { SuccessMessage } from 'src/auth/settings/SuccessMessage';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { showError } from 'src/notifications/notifications';
import { useSessionData } from 'src/auth/SessionDataHook';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';

type SettingsState = 'loading' | 'ready' | 'no_session';

export const SettingsPageRoute = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFlowId = searchParams.get('flow');
  const source = searchParams.get('source');
  const [state, setState] = useState<SettingsState>('loading');
  const [createdFlowId, setCreatedFlowId] = useState<string | null>(null);
  const creatingFlowRef = useRef(false);

  const flowId = urlFlowId || createdFlowId;

  useEffect(() => {
    const checkSessionAndCreateFlow = async () => {
      try {
        await ory.toSession();

        if (!urlFlowId && !createdFlowId && !creatingFlowRef.current) {
          creatingFlowRef.current = true;
          const { data } = await ory.createBrowserSettingsFlow();
          setCreatedFlowId(data.id);
          setSearchParams({ flow: data.id }, { replace: true });
          setState('ready');
          return;
        }

        if (flowId) {
          setState('ready');
        }
      } catch (err) {
        Logger.error('No session found for settings page', err);
        showError('Your session has expired. Please log in again.');
        setState('no_session');
      }
    };

    checkSessionAndCreateFlow();
  }, [urlFlowId, createdFlowId, setSearchParams, flowId]);

  if (state === 'loading') {
    return <AuthPageLayout isLoading />;
  }

  if (state === 'no_session') {
    window.location.replace('/login');
    return null;
  }

  if (!flowId) {
    return <AuthPageLayout isLoading />;
  }

  return <SettingsPage flowId={flowId} source={source} />;
};

interface SettingsPageProps {
  flowId: string;
  source: string | null;
}

const SettingsPage = ({ flowId, source }: SettingsPageProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { isLoading, error, oidcNodes, csrfToken, formAction, updatePassword, initialSuccess } =
    useSettingsFlow(flowId);

  const { session, isLoading: isSessionLoading } = useSessionData();
  const tenantName = session?.identity?.metadata_public?.tenantId;
  const { config: tenantConfig, isLoading: isConfigLoading } = usePublicTenantConfig(tenantName);

  const isLoadingTenantConfig = isSessionLoading || isConfigLoading;

  const displaySuccess = showSuccess || initialSuccess;

  const handleUpdatePassword = async (password: string) => {
    setIsSubmitting(true);
    const { success } = await updatePassword(password);
    setIsSubmitting(false);

    if (success) {
      setShowSuccess(true);
    }
  };

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      {displaySuccess ? (
        <SuccessMessage source={source} />
      ) : (
        <ChangePasswordForm
          error={error}
          isSubmitting={isSubmitting}
          onSubmit={handleUpdatePassword}
          oidcNodes={oidcNodes}
          formAction={formAction}
          csrfToken={csrfToken}
          tenantConfig={tenantConfig}
          isLoadingTenantConfig={isLoadingTenantConfig}
        />
      )}
    </AuthPageLayout>
  );
};
